import { useState, useEffect, useCallback } from 'react';
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Check, X, CreditCard, Repeat, FileText, PieChart as PieChartIcon, Receipt } from 'lucide-react';
import { api } from '@/lib/api';
import { formatCOP } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie, Legend } from 'recharts';

const TABS = [
  { key: 'dashboard', label: 'Dashboard', icon: TrendingUp },
  { key: 'income', label: 'Ingresos', icon: DollarSign },
  { key: 'fixed', label: 'Gastos Fijos', icon: Repeat },
  { key: 'variable', label: 'Gastos Variables', icon: CreditCard },
  { key: 'subscriptions', label: 'Suscripciones', icon: Receipt },
  { key: 'taxes', label: 'Impuestos', icon: FileText },
  { key: 'reports', label: 'Reportes', icon: PieChartIcon },
];
const PIE_COLORS = ['#3B82F6', '#F59E0B', '#A38560', '#EF4444'];
const inputCls = "bg-[#0A1F1A] border-[#1A3B32] text-white placeholder:text-white/30 focus:border-[#A38560] h-10 rounded-xl text-sm";
const selectCls = "bg-[#0A1F1A] border-[#1A3B32] text-white";

function MonthSelector({ month, onChange }) {
  const shift = (dir) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + dir, 1);
    onChange(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };
  const label = new Date(month + '-15').toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => shift(-1)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/50"><ChevronLeft size={16} /></button>
      <span className="text-sm font-medium text-white capitalize min-w-[140px] text-center">{label}</span>
      <button onClick={() => shift(1)} className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-white/50"><ChevronRight size={16} /></button>
    </div>
  );
}

export default function FinanceModule() {
  const now = new Date(); const defMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [tab, setTab] = useState('dashboard');
  const [month, setMonth] = useState(defMonth);
  const [dash, setDash] = useState(null);
  const [income, setIncome] = useState(null);
  const [fixed, setFixed] = useState(null);
  const [variable, setVariable] = useState(null);
  const [subs, setSubs] = useState(null);
  const [taxes, setTaxes] = useState(null);
  const [reports, setReports] = useState(null);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});

  const loadTab = useCallback(async () => {
    try {
      if (tab === 'dashboard') setDash(await api.get(`/finance/dashboard?month=${month}`));
      if (tab === 'income') setIncome(await api.get(`/finance/income?month=${month}`));
      if (tab === 'fixed') setFixed(await api.get(`/finance/fixed?month=${month}`));
      if (tab === 'variable') setVariable(await api.get(`/finance/variable?month=${month}`));
      if (tab === 'subscriptions') setSubs(await api.get('/finance/subscriptions'));
      if (tab === 'taxes') setTaxes(await api.get(`/finance/taxes?month=${month}`));
      if (tab === 'reports') setReports(await api.get('/finance/reports?months=6'));
    } catch { toast.error('Error cargando datos'); }
  }, [tab, month]);

  useEffect(() => { loadTab(); }, [loadTab]);

  const save = async () => {
    try {
      const { _type, _id, ...data } = form;
      if (_id) await api.put(`/finance/${_type}/${_id}`, data);
      else await api.post(`/finance/${_type}`, data);
      toast.success('Guardado'); setModal(null); loadTab();
    } catch { toast.error('Error'); }
  };
  const del = async (type, id) => {
    if (!window.confirm('Eliminar?')) return;
    try { await api.delete(`/finance/${type}/${id}`); toast.success('Eliminado'); loadTab(); } catch { toast.error('Error'); }
  };
  const toggleStatus = async (type, id, currentStatus, field = 'status') => {
    const next = currentStatus === 'Pendiente' ? 'Pagado' : currentStatus === 'Por cobrar' ? 'Recibido' : currentStatus;
    try {
      if (type === 'income') await api.put(`/finance/income/${id}/status`, { status: next });
      else await api.put(`/finance/${type}/${id}`, { [field]: next });
      loadTab();
    } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-5" data-testid="finance-module">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <h1 className="font-outfit text-2xl font-semibold text-white">Gestion Financiera</h1>
        <MonthSelector month={month} onChange={setMonth} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto bg-[#0A1F1A] rounded-xl border border-[#1A3B32] p-1 w-fit">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`fin-tab-${t.key}`}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs whitespace-nowrap transition-colors ${tab === t.key ? 'bg-[#A38560] text-[#03110D] font-medium' : 'text-white/50 hover:text-white'}`}>
            <t.icon size={13} /> {t.label}
          </button>
        ))}
      </div>

      {/* Dashboard */}
      {tab === 'dashboard' && dash && <DashboardView data={dash} />}
      {tab === 'dashboard' && !dash && <Spinner />}

      {/* Income */}
      {tab === 'income' && income && (
        <div className="space-y-4">
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
            <p className="text-xs text-white/40 uppercase tracking-wider">Total Ingresos del Mes</p>
            <p className="text-2xl font-outfit font-semibold text-green-400 mt-1">{formatCOP(income.total)}</p>
          </div>
          <Table headers={['Fecha', 'Tipo', 'Proyecto', 'Cliente', 'Monto Selecto', 'Estado']}
            rows={income.items.map(i => [i.date, i.type === 'venta' ? 'Venta' : 'Arrendamiento', i.project, i.client, formatCOP(i.amount),
              <button onClick={() => toggleStatus('income', i.id, i.status)} className={`text-xs px-2 py-1 rounded-lg border ${i.status === 'Recibido' ? 'bg-green-500/10 border-green-500/20 text-green-400' : 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400'}`}>{i.status}</button>
            ])} />
        </div>
      )}

      {/* Fixed Expenses */}
      {tab === 'fixed' && fixed && (
        <ExpenseSection title="Gastos Fijos" total={fixed.total} items={fixed.items} type="fixed"
          onAdd={() => { setForm({ _type: 'fixed', name: '', provider: '', amount: '', status: 'Pendiente', month }); setModal('fixed'); }}
          onEdit={(i) => { setForm({ _type: 'fixed', _id: i.id, name: i.name, provider: i.provider, amount: i.amount, status: i.status }); setModal('fixed'); }}
          onDel={(id) => del('fixed', id)} onToggle={(id, s) => toggleStatus('fixed', id, s)}
          cols={['Nombre', 'Proveedor', 'Monto', 'Estado']}
          render={(i) => [i.name, i.provider, formatCOP(i.amount)]} />
      )}

      {/* Variable Expenses */}
      {tab === 'variable' && variable && (
        <ExpenseSection title="Gastos Variables" total={variable.total} items={variable.items} type="variable"
          onAdd={() => { setForm({ _type: 'variable', name: '', category: '', amount: '', date: '', status: 'Pendiente' }); setModal('variable'); }}
          onEdit={(i) => { setForm({ _type: 'variable', _id: i.id, name: i.name, category: i.category, amount: i.amount, date: i.date, status: i.status }); setModal('variable'); }}
          onDel={(id) => del('variable', id)} onToggle={(id, s) => toggleStatus('variable', id, s)}
          cols={['Nombre', 'Categoria', 'Monto', 'Fecha', 'Estado']}
          render={(i) => [i.name, i.category, formatCOP(i.amount), i.date]} />
      )}

      {/* Subscriptions */}
      {tab === 'subscriptions' && subs && (
        <ExpenseSection title="Suscripciones" total={subs.filter(s => s.status === 'Activa').reduce((a, s) => a + (s.amount || 0), 0)} items={subs} type="subscriptions"
          onAdd={() => { setForm({ _type: 'subscriptions', name: '', provider: '', amount: '', periodicity: 'mensual', renewal_date: '', status: 'Activa' }); setModal('subscriptions'); }}
          onEdit={(i) => { setForm({ _type: 'subscriptions', _id: i.id, name: i.name, provider: i.provider, amount: i.amount, periodicity: i.periodicity, renewal_date: i.renewal_date, status: i.status }); setModal('subscriptions'); }}
          onDel={(id) => del('subscriptions', id)} onToggle={(id, s) => toggleStatus('subscriptions', id, s, 'status')}
          cols={['Nombre', 'Proveedor', 'Monto', 'Periodicidad', 'Renovacion', 'Estado']}
          render={(i) => [i.name, i.provider, formatCOP(i.amount), i.periodicity === 'mensual' ? 'Mensual' : 'Anual', i.renewal_date]}
          statusMap={{ Activa: 'green', Cancelada: 'red' }} />
      )}

      {/* Taxes */}
      {tab === 'taxes' && taxes && (
        <ExpenseSection title="Impuestos y Obligaciones" total={taxes.total} items={taxes.items} type="taxes"
          onAdd={() => { setForm({ _type: 'taxes', tax_type: 'ICA', period: '', due_date: '', amount: '', status: 'Pendiente' }); setModal('taxes'); }}
          onEdit={(i) => { setForm({ _type: 'taxes', _id: i.id, tax_type: i.tax_type, period: i.period, due_date: i.due_date, amount: i.amount, status: i.status }); setModal('taxes'); }}
          onDel={(id) => del('taxes', id)} onToggle={(id, s) => toggleStatus('taxes', id, s)}
          cols={['Tipo', 'Periodo', 'Vencimiento', 'Monto', 'Estado']}
          render={(i) => [i.tax_type, i.period, i.due_date, formatCOP(i.amount)]} />
      )}

      {/* Reports */}
      {tab === 'reports' && reports && <ReportsView data={reports} />}

      {/* Modal */}
      <Dialog open={!!modal} onOpenChange={() => setModal(null)}><DialogContent className="bg-[#0A1F1A] border-[#1A3B32] text-white max-w-md">
        <DialogHeader><DialogTitle className="font-outfit">{form._id ? 'Editar' : 'Nuevo'} Registro</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {modal === 'fixed' && <>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Nombre</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Proveedor</Label><Input value={form.provider || ''} onChange={e => setForm({ ...form, provider: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Monto (COP)</Label><Input type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className={inputCls} /></div>
          </>}
          {modal === 'variable' && <>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Nombre</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Categoria</Label><Input value={form.category || ''} onChange={e => setForm({ ...form, category: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Monto (COP)</Label><Input type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Fecha</Label><Input type="date" value={form.date || ''} onChange={e => setForm({ ...form, date: e.target.value })} className={inputCls} /></div>
          </>}
          {modal === 'subscriptions' && <>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Nombre</Label><Input value={form.name || ''} onChange={e => setForm({ ...form, name: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Proveedor</Label><Input value={form.provider || ''} onChange={e => setForm({ ...form, provider: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Monto (COP)</Label><Input type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Periodicidad</Label>
              <Select value={form.periodicity || 'mensual'} onValueChange={v => setForm({ ...form, periodicity: v })}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className={selectCls}><SelectItem value="mensual">Mensual</SelectItem><SelectItem value="anual">Anual</SelectItem></SelectContent>
              </Select></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Fecha Renovacion</Label><Input type="date" value={form.renewal_date || ''} onChange={e => setForm({ ...form, renewal_date: e.target.value })} className={inputCls} /></div>
          </>}
          {modal === 'taxes' && <>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Tipo</Label>
              <Select value={form.tax_type || 'ICA'} onValueChange={v => setForm({ ...form, tax_type: v })}>
                <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                <SelectContent className={selectCls}>{['ICA', 'Renta', 'ReteICA', 'IVA', 'Timbre', 'Otro'].map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Periodo Gravable</Label><Input value={form.period || ''} onChange={e => setForm({ ...form, period: e.target.value })} className={inputCls} placeholder="Ej: Q1 2026" /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Fecha Vencimiento</Label><Input type="date" value={form.due_date || ''} onChange={e => setForm({ ...form, due_date: e.target.value })} className={inputCls} /></div>
            <div><Label className="text-white/50 text-xs uppercase mb-1 block">Monto (COP)</Label><Input type="number" value={form.amount || ''} onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} className={inputCls} /></div>
          </>}
        </div>
        <DialogFooter><button onClick={save} className="bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-6 py-2.5 rounded-xl text-sm w-full">Guardar</button></DialogFooter>
      </DialogContent></Dialog>
    </div>
  );
}

function Spinner() { return <div className="flex justify-center py-12"><div className="animate-spin w-8 h-8 border-2 border-[#A38560] border-t-transparent rounded-full" /></div>; }

function DashboardView({ data }) {
  const healthColors = { green: 'text-green-400', yellow: 'text-yellow-400', red: 'text-red-400' };
  const healthBg = { green: 'bg-green-500/10 border-green-500/20', yellow: 'bg-yellow-500/10 border-yellow-500/20', red: 'bg-red-500/10 border-red-500/20' };
  const bd = data.breakdown;
  const pieData = [{ name: 'Fijos', value: bd.fixed }, { name: 'Variables', value: bd.variable }, { name: 'Suscripciones', value: bd.subscriptions }, { name: 'Impuestos', value: bd.taxes }].filter(d => d.value > 0);
  return (
    <div className="space-y-4">
      {/* P&G Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card label="Ingresos" value={formatCOP(data.income)} color="text-green-400" icon={<TrendingUp size={16} className="text-green-400" />} />
        <Card label="Egresos" value={formatCOP(data.expenses)} color="text-red-400" icon={<TrendingDown size={16} className="text-red-400" />} />
        <Card label="Utilidad Neta" value={formatCOP(data.utility)} color={healthColors[data.health]} icon={<DollarSign size={16} className={healthColors[data.health]} />} />
        <div className={`rounded-2xl border p-3 ${healthBg[data.health]}`}>
          <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1">Salud Financiera</p>
          <p className={`text-xl font-outfit font-semibold ${healthColors[data.health]}`}>{data.margin}%</p>
          <p className="text-[10px] text-white/30">Margen</p>
        </div>
      </div>
      {/* Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
          <p className="text-sm font-medium text-white mb-3">Egresos por Categoria</p>
          <div className="space-y-2">
            {[{label: 'Gastos Fijos', val: bd.fixed, color: '#3B82F6'}, {label: 'Gastos Variables', val: bd.variable, color: '#F59E0B'}, {label: 'Suscripciones', val: bd.subscriptions, color: '#A38560'}, {label: 'Impuestos', val: bd.taxes, color: '#EF4444'}].map(c => (
              <div key={c.label} className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: c.color }} />
                <span className="text-xs text-white/60 flex-1">{c.label}</span>
                <span className="text-xs text-white font-medium">{formatCOP(c.val)}</span>
              </div>
            ))}
          </div>
        </div>
        {pieData.length > 0 && (
          <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
            <p className="text-sm font-medium text-white mb-3">Distribucion</p>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart><Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} innerRadius={40} paddingAngle={2}>
                  {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie>
                  <Tooltip contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid #1A3B32', borderRadius: 12, color: '#fff' }} formatter={v => formatCOP(v)} /><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
      {/* Alerts */}
      {data.alerts.length > 0 && (
        <div className="bg-yellow-500/5 rounded-2xl border border-yellow-500/15 p-4">
          <p className="text-sm font-medium text-yellow-400 mb-2 flex items-center gap-2"><AlertTriangle size={16} /> Alertas (proximos 7 dias)</p>
          {data.alerts.map((a, i) => (
            <div key={i} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
              <span className="text-xs text-white/60">{a.type === 'subscription' ? 'Suscripcion' : 'Impuesto'}</span>
              <span className="text-xs text-white font-medium flex-1">{a.name}</span>
              <span className="text-xs text-yellow-400">{a.date}</span>
              <span className="text-xs text-white/40">{formatCOP(a.amount)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Card({ label, value, color = 'text-white', icon }) {
  return (
    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-3">
      <div className="flex items-center gap-2 mb-1">{icon}<span className="text-[10px] text-white/40 uppercase tracking-wider">{label}</span></div>
      <p className={`text-xl font-outfit font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden overflow-x-auto">
      <table className="w-full"><thead><tr className="border-b border-white/5">
        {headers.map(h => <th key={h} className="text-left p-3 text-xs text-white/40 uppercase whitespace-nowrap">{h}</th>)}
      </tr></thead><tbody>
        {rows.map((row, i) => <tr key={i} className="border-b border-white/5 last:border-0">
          {row.map((cell, j) => <td key={j} className="p-3 text-sm text-white/70 whitespace-nowrap">{cell}</td>)}
        </tr>)}
        {rows.length === 0 && <tr><td colSpan={headers.length} className="p-6 text-center text-white/30 text-sm">Sin registros</td></tr>}
      </tbody></table>
    </div>
  );
}

function ExpenseSection({ title, total, items, type, onAdd, onEdit, onDel, onToggle, cols, render, statusMap }) {
  const sm = statusMap || { Pendiente: 'yellow', Pagado: 'green' };
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs text-white/40 uppercase tracking-wider">Total del Mes</p>
          <p className="text-2xl font-outfit font-semibold text-red-400">{formatCOP(total)}</p>
        </div>
        <button onClick={onAdd} className="flex items-center gap-2 bg-gradient-to-r from-[#A38560] to-[#8B6F47] text-[#03110D] font-semibold px-4 py-2.5 rounded-xl text-sm"><Plus size={16} /> Agregar</button>
      </div>
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 overflow-hidden overflow-x-auto">
        <table className="w-full"><thead><tr className="border-b border-white/5">
          {cols.map(c => <th key={c} className="text-left p-3 text-xs text-white/40 uppercase whitespace-nowrap">{c}</th>)}
          <th className="p-3 text-xs text-white/40 uppercase w-20">Acciones</th>
        </tr></thead><tbody>
          {items.map(i => {
            const cells = render(i);
            const status = i.status || 'Pendiente';
            const sColor = sm[status] || 'gray';
            return (
              <tr key={i.id} className="border-b border-white/5 last:border-0">
                {cells.map((c, j) => <td key={j} className="p-3 text-sm text-white/70 whitespace-nowrap">{c}</td>)}
                <td className="p-3">
                  <button onClick={() => onToggle(i.id, status)} className={`text-xs px-2 py-1 rounded-lg border ${sColor === 'green' ? 'bg-green-500/10 border-green-500/20 text-green-400' : sColor === 'yellow' ? 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400' : 'bg-red-500/10 border-red-500/20 text-red-400'}`}>{status}</button>
                </td>
                <td className="p-3">
                  <div className="flex gap-1">
                    <button onClick={() => onEdit(i)} className="p-1.5 rounded-lg hover:bg-white/5 text-white/30 hover:text-white"><Pencil size={14} /></button>
                    <button onClick={() => onDel(i.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-white/30 hover:text-red-400"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            );
          })}
          {items.length === 0 && <tr><td colSpan={cols.length + 2} className="p-6 text-center text-white/30 text-sm">Sin registros</td></tr>}
        </tbody></table>
      </div>
    </div>
  );
}

function ReportsView({ data }) {
  const { monthly } = data;
  const barData = monthly.map(m => ({ name: new Date(m.month + '-15').toLocaleDateString('es-CO', { month: 'short' }), Ingresos: m.income, Egresos: m.expenses }));
  const current = monthly[monthly.length - 1] || {};
  const pieData = [{ name: 'Fijos', value: current.fixed || 0 }, { name: 'Variables', value: current.variable || 0 }, { name: 'Suscripciones', value: current.subscriptions || 0 }, { name: 'Impuestos', value: current.taxes || 0 }].filter(d => d.value > 0);
  return (
    <div className="space-y-4">
      {/* P&G Table */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
        <p className="text-sm font-medium text-white mb-3">Estado de Resultados (P&G)</p>
        <table className="w-full"><tbody className="text-sm">
          {monthly.slice().reverse().map(m => (
            <tr key={m.month} className="border-b border-white/5">
              <td className="py-2 text-white/40 capitalize">{new Date(m.month + '-15').toLocaleDateString('es-CO', { month: 'long', year: 'numeric' })}</td>
              <td className="py-2 text-green-400 text-right">{formatCOP(m.income)}</td>
              <td className="py-2 text-red-400 text-right">{formatCOP(m.expenses)}</td>
              <td className={`py-2 text-right font-medium ${m.utility >= 0 ? 'text-green-400' : 'text-red-400'}`}>{formatCOP(m.utility)}</td>
              <td className="py-2 text-white/30 text-right">{m.income > 0 ? Math.round(m.utility / m.income * 100) : 0}%</td>
            </tr>
          ))}
        </tbody></table>
      </div>
      {/* Comparative Chart */}
      <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
        <p className="text-sm font-medium text-white mb-3">Comparativo Mensual</p>
        <div className="h-[260px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={barData}><CartesianGrid strokeDasharray="3 3" stroke="#1A3B32" />
              <XAxis dataKey="name" stroke="#ffffff40" tick={{ fontSize: 11 }} /><YAxis stroke="#ffffff40" tick={{ fontSize: 10 }} />
              <Tooltip contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid #1A3B32', borderRadius: 12, color: '#fff' }} formatter={v => formatCOP(v)} />
              <Bar dataKey="Ingresos" fill="#22C55E" radius={[4, 4, 0, 0]} /><Bar dataKey="Egresos" fill="#EF4444" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Pie */}
      {pieData.length > 0 && (
        <div className="bg-[#0A1F1A] rounded-2xl border border-white/5 p-4">
          <p className="text-sm font-medium text-white mb-3">Desglose de Egresos (mes actual)</p>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} innerRadius={45} paddingAngle={2} label={({ name, value }) => `${name}: ${Math.round(value / pieData.reduce((a, d) => a + d.value, 0) * 100)}%`}>
                {pieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie>
                <Tooltip contentStyle={{ backgroundColor: '#0A1F1A', border: '1px solid #1A3B32', borderRadius: 12, color: '#fff' }} formatter={v => formatCOP(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
